import { useHandler } from "@chatbotgang/etude/react/useHandler";
import { css } from "@emotion/react";
import Stack from "@mui/material/Stack";
import { theme } from "@polifonia/theme";
import { useDropzone } from "react-dropzone";
import { useTranslation } from "react-i18next";

import { Button } from "@/components/Button";
import { MotifIcon } from "@/components/MotifIcon";
import { Typography } from "@/components/Typography";

const cssFileUploadArea = css`
  border: 2px dashed ${theme.colors.staticFgLine};
  border-radius: 8px;
  padding: 48px 24px;
  text-align: center;
  cursor: pointer;
  transition: all 0.2s ease;
  background-color: ${theme.colors.white};

  &:hover {
    border-color: ${theme.colors.colorBlue60};
    background-color: ${theme.colors.staticBgPrimary};
  }

  &.dragover {
    border-color: ${theme.colors.colorBlue60};
    background-color: ${theme.colors.staticBgPrimary};
  }
`;

const cssFileUploadIcon = css`
  font-size: 48px;
  color: ${theme.colors.staticFgNote};
  margin-bottom: 16px;
`;

const cssFileInfo = css`
  margin-top: 16px;
  padding: 12px;
  background-color: ${theme.colors.staticBgContainer};
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const cssFileStack = css`
  flex: 1;
  min-width: 0;
`;

const cssFileName = css`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const cssDescription = css`
  color: ${theme.colors.staticFgNote};
`;

interface FileDropZoneProps {
  selectedFile: File | null;
  disabled?: boolean;
  onFileSelect: (file: File) => void;
  onRemoveFile: () => void;
}

export const FileDropZone: React.FC<FileDropZoneProps> = function FileDropZone({
  selectedFile,
  disabled = false,
  onFileSelect,
  onRemoveFile,
}) {
  const { t } = useTranslation();

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles: Array<File>) => {
      const file = acceptedFiles[0];
      if (file) {
        onFileSelect(file);
      }
    },
    accept: {
      "text/csv": [".csv"],
      "application/xml": [".xml"],
      "text/xml": [".xml"],
      "text/tab-separated-values": [".tsv"],
      "text/plain": [".txt"],
    },
    multiple: false,
    disabled,
  });

  const handleRemoveFileClick = useHandler((e: React.MouseEvent) => {
    e.stopPropagation();
    onRemoveFile();
  });

  return (
    <div
      {...getRootProps()}
      css={cssFileUploadArea}
      className={isDragActive ? "dragover" : undefined}
    >
      <input {...getInputProps()} />
      {selectedFile ? (
        <div css={cssFileInfo}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="center"
            css={cssFileStack}
          >
            <MotifIcon un-i-motif="file" />
            <Typography
              variant="body"
              css={cssFileName}
              title={selectedFile.name}
            >
              {selectedFile.name}
            </Typography>
          </Stack>
          <Button
            variant="transparent"
            onClick={handleRemoveFileClick}
            disabled={disabled}
          >
            <MotifIcon un-i-motif="cross" />
          </Button>
        </div>
      ) : (
        <>
          <MotifIcon un-i-motif="arrow_up" css={cssFileUploadIcon} />
          <Typography variant="body" css={cssDescription}>
            {t("productFeed.fileUpload.dropZone.text")}
          </Typography>
          <Typography variant="body" css={cssDescription}>
            {t("productFeed.fileUpload.dropZone.formatNote")}
          </Typography>
        </>
      )}
    </div>
  );
};
